/**
 * Profit page — V5.2 layout (historical analysis sidebar tab).
 * Demo: fixtures. Live: truth v2 ops attribution (Phase 1 — not Xero books).
 */

import FigureNote from './FigureNote';
import AboveTooltip from './AboveTooltip';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Download, MapPin, PoundSterling, RefreshCw, Settings, Sparkles } from 'lucide-react';
import BarChart from '../BarChart';
import ControlCentreSourceBadge from './ControlCentreSourceBadge';
import ProfitVehicleDayMapModal from '../profitability/ProfitVehicleDayMapModal';
import {
  DEMO_PROFIT_MONTHS,
  formatProfitGbp,
  isNegativeMoneyCell,
  isPositiveMoneyCell,
  type ProfitDetailDim,
  type ProfitDetailRowMeta,
  type ProfitReportPeriod,
} from '../../data/mockProfitPage';
import { API_ENDPOINTS } from '../../config/api';
import { useProfitPageBundle } from '../../hooks/useProfitPageBundle';
import { STATUS_COLORS } from '../../config/colors';
import {
  fetchTruthProfitMonths,
  fetchTruthTelematicsDeliveryStops,
  fetchTruthTelematicsRouteSegments,
} from '../../services/profitabilityTruthClient';
import type { TruthMergedCostRow, TruthMergedOrderRow } from '../../types/profitabilityTruth';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { emptyProfitReportShell } from '../../utils/emptyProfitReportShell';
import { customMonthOf, customMonthPeriod, monthLabel } from '../../utils/profitReportPeriodRange';
import { mapOrgDepotsFromApiPayload, type OrgDepotForMap } from '../../utils/mapOrderVehicleRole';
import { isSessionAdmin } from '../../utils/pageAccess';
import { missingVehicleKind, missingVehicleLabel } from '../../utils/orderCancellation';
import { COLLECTED_BY_HEAD, DELIVERED_BY_HEAD, DIRECTION_HEAD, dailyAxisLabel, NEAR_ZERO_MILES_CELL, NO_GPS_CELL, NO_JOBS_CELL, ZERO_MILES_CELL, jobFigureTitle, networkKpiKnown, noMileageDaysNote, revenueImputationMonthsText, revenueImputationSingleText, truthMergedOrderToMapOrder } from '../../utils/profitPageFromTruth';

/** Direction pills: COL (we collect, network delivers) vs DEL (network collects, we deliver). */
const DIRECTION_PILL: Record<string, string> = {
  COL: 'bg-ring/15 text-ring border-ring/40',
  DEL: 'bg-chart-5/15 text-chart-5 border-chart-5/40',
  LOCAL: 'bg-accent/15 text-foreground border-accent/40',
};
import { sortDetailRowIndices } from '../../utils/profitDetailSort';
import type {
  ProfitMapOrder,
  RouteSegment,
  TelematicsDeliveryStop,
} from '../../utils/profitVehicleDayMap';

function formatKpiMoney(value: number, ready: boolean): string {
  if (!ready) return '—';
  return formatProfitGbp(value);
}

const REPORT_PERIOD_OPTIONS: Array<{ id: ProfitReportPeriod; label: string }> = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last-week', label: 'Last week' },
  { id: 'month-to-date', label: 'Month to date' },
  { id: 'last-month', label: 'Last month' },
];

/** Today is shown disabled so operators see why it is greyed (day not closed). */
const SHOW_TODAY_DISABLED = true;

const DETAIL_DIMS: Array<{ id: ProfitDetailDim; label: string }> = [
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'job', label: 'Job' },
  { id: 'customer', label: 'Customer' },
  { id: 'postcode', label: 'Postcode' },
  { id: 'run', label: 'Run' },
];

/** Same page size as Orders daily drilldown table. */
const DETAIL_ROWS_PER_PAGE = 10;

function csvEscapeCell(cell: string): string {
  return `"${String(cell).replace(/"/g, '""')}"`;
}

function downloadProfitDetailCsv(
  heads: string[],
  rows: string[][],
  detailDim: ProfitDetailDim,
  range: { startDate: string; endDate: string },
) {
  const lines = [
    heads.map(csvEscapeCell).join(','),
    ...rows.map((row) => row.map(csvEscapeCell).join(',')),
  ];
  // UTF-8 BOM so Excel (Windows) keeps £ and − instead of showing "?".
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `profit-${detailDim}_${range.startDate}_${range.endDate}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function PeriodChips<T extends string>(props: {
  value: T;
  onChange: (id: T) => void;
  options: Array<{ id: T; label: string }>;
  ariaLabel: string;
  accent?: 'green' | 'purple';
  leadingDisabled?: { label: string; title: string };
}) {
  const active =
    props.accent === 'purple'
      ? 'bg-chart-5 text-white font-semibold'
      : 'bg-primary text-white font-semibold';
  return (
    <div
      className="inline-flex rounded-lg border border-border bg-background/70 p-1"
      role="group"
      aria-label={props.ariaLabel}
    >
      {props.leadingDisabled && (
        <button
          type="button"
          disabled
          title={props.leadingDisabled.title}
          className="px-3 py-1.5 text-sm rounded-md text-muted-foreground cursor-not-allowed"
        >
          {props.leadingDisabled.label}
        </button>
      )}
      {props.options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={props.value === o.id}
          onClick={() => props.onChange(o.id)}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
            props.value === o.id ? active : 'text-foreground hover:text-white hover:bg-secondary/80'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Custom period: any calendar month with stored profit (GET /profitability/v2/months).
 * Looks like a chip; green when a custom month is the active period.
 */
function CustomMonthSelect(props: {
  value: ProfitReportPeriod;
  onChange: (period: ProfitReportPeriod) => void;
  disabled?: boolean;
  /** Demo mode: offer the months the fixtures cover, not stored months. */
  demo?: boolean;
}) {
  const [storedMonths, setMonths] = useState<Array<{ month: string; days: number }> | null>(null);
  const months = props.demo ? DEMO_PROFIT_MONTHS : storedMonths;
  const [error, setError] = useState<string | null>(null);
  const selected = customMonthOf(props.value);

  const load = useCallback(() => {
    if (props.demo || storedMonths !== null || props.disabled) return;
    const ac = new AbortController();
    fetchTruthProfitMonths({ signal: ac.signal })
      .then((m) => setMonths(m))
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load months');
        setMonths([]);
      });
  }, [props.demo, storedMonths, props.disabled]);

  // A custom month restored from elsewhere still needs its option listed.
  useEffect(() => {
    if (selected) load();
  }, [selected, load]);

  const active = selected != null;
  const title = props.disabled
    ? 'Custom months are not available'
    : props.demo
      ? 'Choose a demo month'
      : error
      ? `Could not load months: ${error}`
      : 'Choose any month with stored profit data';
  return (
    <label
      className={`inline-flex items-center rounded-lg border p-1 ${
        active ? 'border-success bg-success' : 'border-accent bg-background/70'
      } ${props.disabled ? 'opacity-50' : ''}`}
      title={title}
    >
      <span className="sr-only">Custom month</span>
      <select
        value={selected ?? ''}
        disabled={props.disabled}
        onFocus={load}
        onMouseDown={load}
        onChange={(e) => {
          if (e.target.value) props.onChange(customMonthPeriod(e.target.value));
        }}
        className={`cursor-pointer appearance-none rounded-md bg-transparent px-3 py-1.5 pr-7 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-success ${
          active ? 'font-semibold text-white' : 'text-foreground hover:text-white'
        }`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239ca3af'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0.6rem center',
        }}
      >
        <option value="" disabled className="bg-background text-muted-foreground">
          Custom
        </option>
        {months === null && (
          <option value="" disabled className="bg-background text-muted-foreground">Loading months…</option>
        )}
        {months !== null && months.length === 0 && (
          <option value="" disabled className="bg-background text-muted-foreground">
            {error ? 'Could not load months' : 'No stored months'}
          </option>
        )}
        {(months ?? []).map((m) => (
          <option key={m.month} value={m.month} className="bg-background text-foreground">
            {monthLabel(m.month)} ({m.days} day{m.days === 1 ? '' : 's'})
          </option>
        ))}
        {selected && !(months ?? []).some((m) => m.month === selected) && (
          <option value={selected} className="bg-background text-foreground">{monthLabel(selected)}</option>
        )}
      </select>
    </label>
  );
}

interface ProfitSectionProps {
  demoMode?: boolean;
  /** When set with showDemoGear, admin can toggle demo fixtures. */
  onDemoModeChange?: (enabled: boolean) => void;
  showDemoGear?: boolean;
  /** Admins see live MapPins on Vehicle/Job detail. Defaults to session admin. */
  isAdmin?: boolean;
}

type ProfitMapModalState = {
  row: {
    vehicleId: string;
    date: string;
    periodLabel?: string;
    orderIds?: string[];
    pickupOrderIds?: string[];
    deliveryOrderIds?: string[];
    startDate?: string;
    endDate?: string;
  };
  order: ProfitMapOrder | null;
  stops: TelematicsDeliveryStop[];
  segments: RouteSegment[];
  /** Failed telematics fetch — distinct from confirmed empty GPS. */
  telematicsLoadError: string | null;
};

/** Vehicle table Jobs header tooltip: Jobs can exceed Pickup + Delivery (carried legs). */
const VEHICLE_JOBS_HEAD_HINT =
  'Pickups + deliveries + jobs carried that day without a pickup or delivery (delivered on another day, or still in progress).';

export default function ProfitSection({
  demoMode = false,
  onDemoModeChange,
  showDemoGear = false,
  isAdmin: isAdminProp,
}: ProfitSectionProps) {
  const isAdmin = isAdminProp ?? isSessionAdmin();
  const [reportPeriod, setReportPeriod] = useState<ProfitReportPeriod>('last-week');
  const [notesOn, setNotesOn] = useState(false);
  const [detailDim, setDetailDim] = useState<ProfitDetailDim>('vehicle');
  const [detailPage, setDetailPage] = useState(1);
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [mapStatusMessage, setMapStatusMessage] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const [mapModal, setMapModal] = useState<ProfitMapModalState | null>(null);
  const [mapPinLoadingKey, setMapPinLoadingKey] = useState<string | null>(null);
  const [orgDepotsForMap, setOrgDepotsForMap] = useState<OrgDepotForMap[]>([]);
  const orgDepotsCacheRef = useRef<OrgDepotForMap[] | null>(null);
  const mapOpenGenRef = useRef(0);

  const {
    data,
    empty,
    loading,
    error,
    range,
    orders: truthOrders,
    ordersCoordsPending,
    costs: truthCosts,
    refresh,
  } = useProfitPageBundle(reportPeriod, { demo: demoMode, syncXero: isAdmin });

  const shell = useMemo(
    () => emptyProfitReportShell(reportPeriod, range, demoMode),
    [reportPeriod, range, demoMode],
  );
  const display = data ?? shell;
  const figuresReady = Boolean(data);
  // Shell defaults to ops_attributed — do not paint that (or FROM XERO) until
  // figures arrive. While loading, card titles use Revenue (Xero strip).
  const xeroKpis = figuresReady && display.kpiMode === 'xero_books';
  const opsKpis = figuresReady && display.kpiMode === 'ops_attributed';
  const kpiSourceBadge = xeroKpis ? (
    <span
      title={display.xeroKpiNotice?.booksSource === 'invoice_rollup'
        ? 'Xero invoice rollup (sales less purchase invoices) — narrower than a full P&L'
        : 'Xero profit & loss report'}
      className="shrink-0 rounded-md border border-chart-10/40 bg-chart-10/10 px-1.5 py-0.5 text-xs font-bold tracking-wide text-chart-10">
      From XERO
    </span>
  ) : opsKpis ? (
    <span
      title="Estimated from ops / TMS attribution — not Xero books"
      className="shrink-0 rounded-md border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-xs font-bold tracking-wide text-warning">
      ESTIMATED
    </span>
  ) : null;
  const liveDimsComingSoon = !demoMode;

  useEffect(() => {
    if (!liveDimsComingSoon) return;
    if (detailDim === 'run') {
      setDetailDim('vehicle');
    }
  }, [liveDimsComingSoon, detailDim]);

  useEffect(() => {
    setDetailPage(1);
    setSortCol(null);
    setSortDir('asc');
  }, [detailDim, reportPeriod, demoMode]);

  useEffect(() => {
    if (!mapStatusMessage) return;
    const t = window.setTimeout(() => setMapStatusMessage(null), 3500);
    return () => window.clearTimeout(t);
  }, [mapStatusMessage]);

  useEffect(() => {
    if (!showSettings) return;
    const onDoc = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showSettings]);

  const chartGroups = useMemo(() => {
    if (!figuresReady) return [];
    const count = display.dailySeries.length;
    return display.dailySeries.map((p) => ({
      label: p.label,
      axisLabel: dailyAxisLabel(p, count),
      values: [
        {
          vehicle: 'Net profit',
          value: p.netProfitGbp,
          color: p.netProfitGbp < 0 ? STATUS_COLORS.destructive : STATUS_COLORS.success,
          emphasized: p.highlighted === true,
        },
      ],
    }));
  }, [display.dailySeries, figuresReady]);

  const detailTable = display.detail[detailDim];
  const sortedDetail = useMemo(() => {
    const rows = detailTable.rows;
    const meta = detailTable.rowMeta ?? [];
    if (sortCol == null) {
      return { rows, meta };
    }
    const col = sortCol;
    const indices = sortDetailRowIndices(rows, col, sortDir);
    return {
      rows: indices.map((i) => rows[i]!),
      meta: indices.map((i) => meta[i] as ProfitDetailRowMeta | undefined),
    };
  }, [detailTable.rows, detailTable.rowMeta, sortCol, sortDir]);
  const sortedDetailRows = sortedDetail.rows;
  const sortedDetailMeta = sortedDetail.meta;
  const detailTotalPages = Math.max(1, Math.ceil(sortedDetailRows.length / DETAIL_ROWS_PER_PAGE));
  const safeDetailPage = Math.min(detailPage, detailTotalPages);
  const pagedDetailRows = useMemo(() => {
    const start = (safeDetailPage - 1) * DETAIL_ROWS_PER_PAGE;
    return sortedDetailRows.slice(start, start + DETAIL_ROWS_PER_PAGE);
  }, [sortedDetailRows, safeDetailPage]);
  const pagedDetailMeta = useMemo(() => {
    const start = (safeDetailPage - 1) * DETAIL_ROWS_PER_PAGE;
    return sortedDetailMeta.slice(start, start + DETAIL_ROWS_PER_PAGE);
  }, [sortedDetailMeta, safeDetailPage]);

  const mapOrders = useMemo(
    () => truthOrders.map(truthMergedOrderToMapOrder),
    [truthOrders],
  );
  const mapMergedCosts = useMemo(
    (): Array<{
      date: string;
      vehicle: string;
      revenue_breakdown?: TruthMergedCostRow['revenue_breakdown'];
    }> =>
      truthCosts.map((c) => ({
        date: String(c.date ?? '').slice(0, 10),
        vehicle: String(c.vehicle ?? ''),
        revenue_breakdown: c.revenue_breakdown,
      })),
    [truthCosts],
  );

  const ensureOrgDepots = useCallback(async (): Promise<OrgDepotForMap[]> => {
    if (orgDepotsCacheRef.current) return orgDepotsCacheRef.current;
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.depots());
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Do not cache failure — next pin open can retry.
        return orgDepotsForMap;
      }
      const mapped = mapOrgDepotsFromApiPayload(json);
      orgDepotsCacheRef.current = mapped;
      setOrgDepotsForMap(mapped);
      return mapped;
    } catch {
      return orgDepotsForMap;
    }
  }, [orgDepotsForMap]);

  const openMapFromMeta = useCallback(
    async (meta: ProfitDetailRowMeta, pinKey: string) => {
      if (!isAdmin || demoMode || meta.kind === 'customer') return;
      const gen = ++mapOpenGenRef.current;
      setMapPinLoadingKey(pinKey);
      setMapStatusMessage(null);

      let vehicleId = '';
      let date = '';
      let order: ProfitMapOrder | null = null;
      let orderIds: string[] | undefined;
      let pickupOrderIds: string[] | undefined;
      let deliveryOrderIds: string[] | undefined;

      if (meta.kind === 'vehicle') {
        vehicleId = meta.vehicle;
        date = meta.date;
        orderIds = meta.orderIds;
        pickupOrderIds = meta.pickupOrderIds;
        deliveryOrderIds = meta.deliveryOrderIds;
      } else {
        order = truthMergedOrderToMapOrder(meta.order);
        vehicleId = meta.vehicleObserved || meta.vehiclePickup || order.vehicleId;
        date = meta.date || order.date;
      }

      if (!vehicleId || !date) {
        if (gen === mapOpenGenRef.current) {
          setMapPinLoadingKey(null);
          setMapStatusMessage(
            !vehicleId
              ? 'Cannot open map — this row has no vehicle.'
              : 'Cannot open map — this row has no date.',
          );
        }
        return;
      }

      try {
        const [stopsOutcome, segmentsOutcome, depots] = await Promise.all([
          fetchTruthTelematicsDeliveryStops(date, date).then(
            (rows) => ({ ok: true as const, rows }),
            (err: unknown) => ({
              ok: false as const,
              message: err instanceof Error ? err.message : 'Stops request failed',
            }),
          ),
          fetchTruthTelematicsRouteSegments(date, date).then(
            (rows) => ({ ok: true as const, rows }),
            (err: unknown) => ({
              ok: false as const,
              message: err instanceof Error ? err.message : 'Route segments request failed',
            }),
          ),
          ensureOrgDepots(),
        ]);
        if (gen !== mapOpenGenRef.current) return;

        const overlayFailed = !stopsOutcome.ok || !segmentsOutcome.ok;
        const stops = stopsOutcome.ok ? (stopsOutcome.rows as TelematicsDeliveryStop[]) : [];
        const segments = segmentsOutcome.ok ? (segmentsOutcome.rows as RouteSegment[]) : [];
        const telematicsLoadError = overlayFailed
          ? [
              !stopsOutcome.ok ? stopsOutcome.message : null,
              !segmentsOutcome.ok ? segmentsOutcome.message : null,
            ]
              .filter(Boolean)
              .join(' · ')
            || 'Could not load telematics from the server — try again shortly.'
          : null;

        setOrgDepotsForMap(depots);
        setMapModal({
          row: {
            vehicleId,
            date,
            startDate: date,
            endDate: date,
            orderIds,
            pickupOrderIds,
            deliveryOrderIds,
          },
          order,
          stops,
          segments,
          telematicsLoadError,
        });
      } finally {
        if (gen === mapOpenGenRef.current) setMapPinLoadingKey(null);
      }
    },
    [demoMode, ensureOrgDepots, isAdmin],
  );

  const handleDetailSort = (colIndex: number) => {
    setDetailPage(1);
    if (sortCol === colIndex) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortCol(colIndex);
    // Money / margin / counts: highest first on first click is more useful for fleet managers.
    const head = detailTable.heads[colIndex] ?? '';
    const preferDesc =
      detailTable.moneyCols.includes(colIndex)
      || head === 'Margin'
      || head === 'Miles'
      || head === 'Pickup'
      || head === 'Delivery'
      || head === 'Lifts'
      || head === 'Jobs'
      || head === 'Vehicles';
    setSortDir(preferDesc ? 'desc' : 'asc');
  };

  // Demo toggle is admin-only, even if a caller passes showDemoGear.
  const demoGear = isAdmin && showDemoGear && onDemoModeChange ? (
    <div className="relative" ref={settingsRef}>
      <button
        type="button"
        onClick={() => setShowSettings((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary hover:bg-accent border border-border text-foreground transition-colors"
        title="Demo settings"
        aria-label="Demo settings"
        aria-expanded={showSettings}
      >
        <Settings className="w-5 h-5" />
      </button>
      {showSettings && (
        <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-xl z-50">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Demo</h3>
            <label className="flex items-center justify-between cursor-pointer group">
              <span className="text-sm text-foreground group-hover:text-white transition-colors">Demo data</span>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={demoMode}
                  onChange={(e) => onDemoModeChange(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-secondary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-chart-5 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-empty after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-muted-foreground after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-chart-5" />
              </div>
            </label>
            <p className="text-xs text-muted-foreground mt-2">
              Show illustrative Profit figures for screenshots / walkthroughs. Turn off for live truth (Xero company KPIs when ingested; otherwise ops attribution).
            </p>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const pageHeader = (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
          <PoundSterling className="w-7 h-7 text-success" />
          Profit
        </h3>

      </div>
      <div className="flex flex-col items-stretch sm:items-end gap-2">
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {demoGear}
          <ControlCentreSourceBadge
            label={display.sourcesLabel}
            prefixLabel={demoMode ? 'Data Sources:' : 'Sources:'}
          />
        </div>
        {!demoMode && (
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="flex items-center gap-1.5 self-end px-3 py-2 rounded-lg bg-secondary hover:bg-accent border border-border text-foreground text-sm disabled:opacity-50"
            title={isAdmin ? 'Sync Xero for this period, then reload figures' : 'Reload figures'}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>
    </div>
  );

  const k = display.kpis;
  const stampLabel = loading
    ? `${range.startDate} → ${range.endDate} · Loading…`
    : figuresReady
      ? k.stampLabel
      : `${range.startDate} → ${range.endDate}`;
  const periodLabel = `${range.startDate} → ${range.endDate}`;
  const hasOpsDetail = truthCosts.length > 0;
  const chartEmptyCopy = (() => {
    if (loading && !figuresReady) {
      return {
        title: 'Loading chart…',
        body: `Fetching profit figures for ${periodLabel}.`,
      };
    }
    if (xeroKpis && !hasOpsDetail) {
      return {
        title: 'No day-by-day bars for this period',
        body: 'Company profit is on the cards above (from Xero). The chart needs vehicle-day projections — run a truth profitability compute for this window, or pick a period with fleet activity.',
      };
    }
    if (empty || !figuresReady) {
      return {
        title: 'No profit chart for this period',
        body: `Nothing stored for ${periodLabel}. Run a truth profitability compute, or choose another period (e.g. Last month).`,
      };
    }
    return {
      title: 'No daily profit points',
      body: 'Ops rows loaded, but there is nothing to plot for this window. Try another period or re-run compute.',
    };
  })();
  const detailDimLabel =
    DETAIL_DIMS.find((d) => d.id === detailDim)?.label ?? 'this view';
  const detailEmptyCopy = (() => {
    if (loading && !figuresReady) {
      return {
        title: 'Loading detail…',
        body: 'Vehicle, job and customer rows appear when figures arrive.',
      };
    }
    if (xeroKpis && !hasOpsDetail) {
      return {
        title: `No ${detailDimLabel.toLowerCase()} rows yet`,
        body: 'Xero fills the company cards only. This table comes from ops attribution (merged costs), not invoices — run a profitability compute for the window, or pick a period with fleet activity.',
      };
    }
    if (empty || !figuresReady) {
      return {
        title: `No ${detailDimLabel.toLowerCase()} breakdown`,
        body: `No stored projections for ${periodLabel}. Run a truth profitability compute, or choose another period.`,
      };
    }
    return {
      title: `No ${detailDimLabel.toLowerCase()} rows for this view`,
      body: 'Try another detail tab, or a period with more activity.',
    };
  })();

  return (
    <div className="space-y-6 min-w-0 max-w-full">
      {pageHeader}

      <div
        className="inline-flex rounded-lg border border-border bg-background/70 p-1"
        role="group"
        aria-label="Profit view"
      >
        <button
          type="button"
          aria-pressed
          className="px-4 py-1.5 text-sm rounded-md transition-colors bg-primary text-white font-semibold"
        >
          Report
        </button>
        <button
          type="button"
          disabled
          title="Coming soon"
          className="px-4 py-1.5 text-sm rounded-md transition-colors inline-flex items-center gap-1.5 text-muted-foreground cursor-not-allowed"
        >
          Brief
          <span className="text-xs opacity-85 inline-flex items-center gap-0.5">
            <Sparkles className="w-3 h-3" /> ZOE
          </span>
        </button>
      </div>

      <>
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <PeriodChips
              value={reportPeriod}
              onChange={setReportPeriod}
              options={REPORT_PERIOD_OPTIONS}
              ariaLabel="Profit time period"
              accent="green"
              leadingDisabled={
                SHOW_TODAY_DISABLED
                  ? {
                      label: 'Today',
                      title: 'Profit for today is not final until the day closes',
                    }
                  : undefined
              }
            />
            <CustomMonthSelect
              value={reportPeriod}
              onChange={setReportPeriod}
              demo={demoMode}
            />
            <span className="text-xs text-muted-foreground">{stampLabel}</span>
            <div className="flex-1" />
            {demoMode && (
              <button
                type="button"
                onClick={() => setNotesOn((v) => !v)}
                className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  notesOn
                    ? 'border-chart-10/40 bg-chart-10/10 text-chart-10'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                Data source notes: {notesOn ? 'on' : 'off'}
              </button>
            )}
          </div>

          {loading && !figuresReady && (
            <p role="status" className="text-sm text-muted-foreground border border-border/60 rounded-lg bg-background/40 px-4 py-3">
              Loading profit figures for {range.startDate} → {range.endDate}…
            </p>
          )}

          {error && !figuresReady && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 flex flex-wrap items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Could not load profit figures</p>
                <p className="text-xs text-muted-foreground mt-1">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => void refresh()}
                className="px-3 py-1.5 rounded-lg bg-secondary hover:bg-accent text-xs text-white shrink-0"
              >
                Try again
              </button>
            </div>
          )}

          {empty && !demoMode && !loading && !figuresReady && (
            <div
              role="status"
              className="rounded-lg border border-dashed border-warning/40 bg-warning/5 px-4 py-3 flex gap-3"
            >
              <PoundSterling className="w-5 h-5 text-warning/80 shrink-0 mt-0.5" />
              <div className="min-w-0 text-sm text-foreground">
                <p className="font-medium text-foreground">No profitability projections for this period</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Nothing is stored for {range.startDate} → {range.endDate}. That is not a £0 fleet —
                  run a truth profitability compute for the window, or choose another period (e.g. Last month).
                </p>
              </div>
            </div>
          )}

          {demoMode && notesOn && (
            <p className="text-xs text-chart-10/90 border-l-2 border-chart-10/50 bg-chart-10/5 pl-3 py-2 rounded-r-md">
              {display.notes.freshness}
            </p>
          )}

          {!demoMode && display.revenueImputation && (
            <div
              role="status"
              className="rounded-lg border border-chart-5/60 border-l-4 bg-chart-5/10 px-4 py-3 text-sm text-foreground"
            >
              <p className="font-semibold text-foreground">Assumed network revenue (£/job)</p>
              <p className="text-sm text-foreground/90 mt-1">
                {Object.keys(display.revenueImputation.monthsUsed ?? {}).length > 0
                  ? `Jobs without revenue use ${revenueImputationMonthsText({ ...display.revenueImputation, filledAtSingleAverage: 0 })}.`
                  : `Assumed average of ${formatProfitGbp(display.revenueImputation.avgGbpPerJob)}/job`}
              </p>
              {Object.keys(display.revenueImputation.monthsUsed ?? {}).length > 0
                && revenueImputationSingleText(display.revenueImputation) ? (
                  <p className="text-sm text-foreground/90 mt-1">{revenueImputationSingleText(display.revenueImputation)}.</p>
                ) : null}
            </div>
          )}

          {!xeroKpis && !demoMode && display.xeroKpiNotice?.kind === 'error' && (
            <p role="alert" className="text-xs text-warning/90 border-l-2 border-warning/40 bg-warning/5 pl-3 py-1.5 rounded-r-md">
              Could not load Xero figures — showing estimates.
            </p>
          )}

          {xeroKpis && (
            <div className="space-y-1.5">

              {isAdmin
                && display.xeroKpiNotice?.kind === 'from_xero'
                && ((display.xeroKpiNotice.unmappedAccountCodes?.length ?? 0) > 0
                  || display.xeroKpiNotice.partialRevenueMapping
                  || (display.xeroKpiNotice.unallocatedPurchaseCreditGbp ?? 0) > 0) && (
                <p className="text-xs text-warning/90 border-l-2 border-warning/40 bg-warning/5 pl-3 py-1.5 rounded-r-md">
                  {display.xeroKpiNotice.partialRevenueMapping
                    ? 'Some ACCREC lines used account codes outside the mapped revenue list (still counted). '
                    : ''}
                  {(display.xeroKpiNotice.unmappedAccountCodes?.length ?? 0) > 0
                    ? `Unmapped codes: ${display.xeroKpiNotice.unmappedAccountCodes!.slice(0, 12).join(', ')}${(display.xeroKpiNotice.unmappedAccountCodes!.length > 12) ? '…' : ''}. `
                    : ''}
                  {(display.xeroKpiNotice.unallocatedPurchaseCreditGbp ?? 0) > 0
                    ? `Purchase credits without line items reduced own costs by £${display.xeroKpiNotice.unallocatedPurchaseCreditGbp!.toFixed(2)} (unbucketed). `
                    : ''}
                </p>
              )}
            </div>
          )}


          <div
            className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 ${loading ? 'opacity-70' : ''}`}
            aria-busy={loading}
          >
            <div className="rounded-lg border border-success/40 bg-gradient-to-br from-success/25 via-card/95 to-card p-4 shadow-sm shadow-black/20">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs uppercase tracking-wide-2 text-muted-foreground font-semibold">Net profit</div>
                {kpiSourceBadge}
              </div>
              <div className="text-4xl font-bold tabular-nums text-success mt-1">
                {formatKpiMoney(k.netProfitGbp, figuresReady)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {k.revenueWithoutTelematics
                  ? `${formatKpiMoney(k.revenueWithoutTelematics.netProfitAllDaysGbp, figuresReady)} including `
                    + `${k.revenueWithoutTelematics.vehicleDays} vehicle-day${k.revenueWithoutTelematics.vehicleDays === 1 ? '' : 's'} `
                    + 'without mileage data (their mileage-based costs are missing)'
                  : opsKpis && !k.networkEstimate?.deducted ? 'Revenue − costs' : 'Revenue − network − costs'}
              </div>
            </div>
            <div className="rounded-lg border border-chart-10/40 bg-gradient-to-br from-chart-10/25 via-card/95 to-card p-4 shadow-sm shadow-black/20">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs uppercase tracking-wide-2 text-muted-foreground font-semibold">Margin</div>
                {kpiSourceBadge}
              </div>
              <div className="text-4xl font-bold tabular-nums text-chart-10 mt-1">
                {figuresReady ? `${k.marginPct}%` : '—'}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {opsKpis ? 'Profit ÷ revenue' : 'Profit ÷ (revenue − network)'}
              </div>
            </div>
            <div className="rounded-lg border border-chart-1/40 bg-gradient-to-br from-chart-1/25 via-card/95 to-card p-4 shadow-sm shadow-black/20">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs uppercase tracking-wide-2 text-muted-foreground font-semibold">Revenue</div>
                {kpiSourceBadge}
              </div>
              <div className="text-4xl font-bold tabular-nums text-white mt-1">
                {formatKpiMoney(k.customerRevenueGbp, figuresReady)}
              </div>
              {k.revenueWithoutTelematics && (
                <div className="text-xs text-muted-foreground mt-1">
                  {`+ ${formatKpiMoney(k.revenueWithoutTelematics.gbp, figuresReady)} on ${k.revenueWithoutTelematics.vehicleDays} `
                    + `day${k.revenueWithoutTelematics.vehicleDays === 1 ? '' : 's'} without recorded mileage data`}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-chart-5/40 bg-gradient-to-br from-chart-5/25 via-card/95 to-card p-4 shadow-sm shadow-black/20">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs uppercase tracking-wide-2 text-muted-foreground font-semibold">Network charges</div>
                {kpiSourceBadge}
              </div>
              <div className="text-4xl font-bold tabular-nums text-white mt-1">
                {opsKpis
                  ? (k.networkEstimate && networkKpiKnown(k.networkEstimate)
                    ? formatKpiMoney(k.networkEstimate.gbp, figuresReady)
                    : '—')
                  : formatKpiMoney(k.networkChargesGbp, figuresReady)}
              </div>
              {opsKpis && (
                <div className="text-xs text-muted-foreground mt-1">
                  {k.networkEstimate
                    ? `${k.networkEstimate.costedOutboundOrders} outbound`
                      // Deducted: unknown = no rate or no depots; order estimate: no rate only.
                      + (k.networkEstimate.outboundWithoutRate > 0
                        ? (k.networkEstimate.deducted
                          ? ` · ${k.networkEstimate.outboundWithoutRate} unknown`
                          : ` · ${k.networkEstimate.outboundWithoutRate} without a rate`)
                        : '')
                      + (k.networkEstimate.deducted ? ' · in net profit' : ' · not deducted')
                    : 'No outbound orders costed'}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-warning/40 bg-gradient-to-br from-warning/25 via-card/95 to-card p-4 shadow-sm shadow-black/20">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs uppercase tracking-wide-2 text-muted-foreground font-semibold">Costs</div>
                {kpiSourceBadge}
              </div>
              <div className="text-4xl font-bold tabular-nums text-white mt-1">
                {formatKpiMoney(k.ownCostsGbp, figuresReady)}
              </div>
            </div>
          </div>

          {demoMode && notesOn && (
            <p className="text-xs text-chart-10/90 border-l-2 border-chart-10/50 bg-chart-10/5 pl-3 py-2 rounded-r-md">
              {display.notes.kpis}
            </p>
          )}

          {figuresReady && display.dataQuality && (
            <div
              role="status"
              className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 flex gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-warning">{display.dataQuality.title}</p>
                <p className="text-xs text-warning/80 mt-1">{display.dataQuality.body}</p>
              </div>
            </div>
          )}

          <section className="min-w-0 max-w-full">
            <h4 className="text-xs font-semibold uppercase tracking-wide-3 text-muted-foreground mb-3">
              {display.chartTitle}
            </h4>
            <div className="rounded-lg border border-border/60 bg-background/40 px-2 pt-2 sm:px-3 sm:pt-3 pb-1 relative min-h-80 overflow-hidden">
              {figuresReady && chartGroups.length > 0 ? (
                <>
                  <BarChart
                    data={chartGroups}
                    height="h-80"
                    compact
                    hideLegend
                    accent="green"
                    yAxisPrefix="£"
                    formatYAxisAsK
                    brightAxisLabels
                    clipPlot
                  />
                  {noMileageDaysNote(display.dailySeries) ? (
                    <p className="px-2 pb-2 text-xs text-warning">{noMileageDaysNote(display.dailySeries)}</p>
                  ) : null}
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-6">
                  <div className="max-w-md text-center space-y-1.5">
                    <p className="text-sm font-medium text-foreground">{chartEmptyCopy.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{chartEmptyCopy.body}</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="min-w-0 max-w-full">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide-3 text-muted-foreground">
                Detail — where the money went
              </h4>
              {demoMode && notesOn && (
                <span className="text-xs font-semibold tracking-wide text-chart-10/90 bg-chart-10/10 border border-chart-10/25 px-2 py-0.5 rounded-full">
                  One table, five views
                </span>
              )}
            </div>
            <div className="rounded-lg border border-border/60 bg-background/40 min-w-0 max-w-full">
              <div className="flex flex-wrap items-center gap-2 p-2 border-b border-border/60 min-w-0">
                <div
                  className="flex flex-wrap gap-1 min-w-0 flex-1"
                  role="tablist"
                  aria-label="Detail dimension"
                >
                {DETAIL_DIMS.map((d) => {
                  const comingSoon = liveDimsComingSoon && d.id === 'run';
                  return (
                    <button
                      key={d.id}
                      type="button"
                      role="tab"
                      aria-selected={detailDim === d.id}
                      disabled={comingSoon}
                      title={comingSoon ? `${d.label} rollup coming soon` : undefined}
                      onClick={() => {
                        if (!comingSoon) setDetailDim(d.id);
                      }}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                        comingSoon
                          ? 'text-muted-foreground cursor-not-allowed'
                          : detailDim === d.id
                            ? 'bg-success/20 text-success ring-1 ring-success/40'
                            : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                      }`}
                    >
                      {d.label}
                      {comingSoon ? ' (Coming Soon)' : ''}
                    </button>
                  );
                })}
                </div>
                <button
                  type="button"
                  disabled={!figuresReady || sortedDetailRows.length === 0}
                  title={
                    !figuresReady
                      ? 'Export available when figures have loaded'
                      : sortedDetailRows.length === 0
                        ? 'No rows to export for this view'
                        : `Export ${detailDim} detail as CSV`
                  }
                  onClick={() =>
                    downloadProfitDetailCsv(
                      detailTable.heads,
                      sortedDetailRows,
                      detailDim,
                      range,
                    )
                  }
                  className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-md border border-border bg-card/80 px-2.5 py-1 text-xs font-medium text-foreground hover:bg-secondary/80 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Download className="w-3.5 h-3.5" aria-hidden />
                  Export CSV
                </button>
              </div>
              {mapStatusMessage && (
                <p
                  role="status"
                  className="text-xs text-warning/90 border-b border-warning/25 bg-warning/10 px-3 py-2"
                >
                  {mapStatusMessage}
                </p>
              )}
              {/* Single horizontal scrollport — scroll-x-only avoids nested vertical scrollbars. */}
              <div className="scroll-x-only w-full min-w-0 max-w-full">
                <table className="w-full min-w-0 table-fixed text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-foreground bg-secondary">
                      {detailTable.heads.map((h, hi) => {
                        const money = detailTable.moneyCols.includes(hi);
                        const isDate = h === 'Date';
                        const isVehicle = h === 'Vehicle';
                        // Fixed layout: wide enough for the 11-digit ID and its map pin.
                        const isTrackingId = h === 'Tracking ID';
                        // Small counts: narrow, so Customer gets the room for names.
                        const isCount = h === 'Lifts' || h === 'Jobs';
                        // Customer table: room for names. Job table: a little more than an equal share,
                        // but Collected / Delivered by must still fit on one line.
                        const isJobTable = detailTable.heads.includes('Tracking ID');
                        const isCustomer = h === 'Customer';
                        const isLegBy = h === COLLECTED_BY_HEAD || h === DELIVERED_BY_HEAD;
                        const isPostcodeDistrict = h === 'Postcode district';
                        const isActive = sortCol === hi;
                        const isVehicleJobs = h === 'Jobs' && detailTable.heads.includes('Pickup');
                        const alignRight = money || h === 'Margin' || h === 'Profit';
                        return (
                          <th
                            key={`${hi}-${h}`}
                            className={`py-2 font-semibold leading-tight ${
                              isDate
                                ? 'w-18 px-1.5'
                                : isVehicle
                                  ? 'w-26 px-1.5'
                                  : isTrackingId
                                    ? 'w-33 px-1.5'
                                    : isCount
                                      ? 'w-14 px-1.5'
                                      : isCustomer
                                        ? `${isJobTable ? 'w-42' : 'w-52'} px-1.5`
                                        : isLegBy
                                          ? 'w-30 px-1.5'
                                  : isPostcodeDistrict
                                    ? 'w-56 min-w-48 px-2'
                                    : 'px-1.5'
                            } ${alignRight ? 'text-right' : ''}`}
                          >
                            {(() => {
                            const sortButton = (
                            <button
                              type="button"
                              onClick={() => handleDetailSort(hi)}
                              title={
                                h === 'Revenue'
                                  ? `Sort by revenue${isActive ? ` (${sortDir})` : ''}`
                                  : h === 'Pickup'
                                    ? `Sort by pickups${isActive ? ` (${sortDir})` : ''}`
                                    : h === 'Delivery'
                                      ? `Sort by deliveries${isActive ? ` (${sortDir})` : ''}`
                                      : h === 'Stand.'
                                        ? `Sort by standing${isActive ? ` (${sortDir})` : ''}`
                                        : isVehicleJobs
                                          // Explained by the tooltip above the header, not a native title.
                                          ? undefined
                                          : `Sort by ${h}${isActive ? ` (${sortDir})` : ''}`
                              }
                              className={`inline-flex items-center gap-0.5 max-w-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-success rounded ${
                                alignRight ? 'ml-auto' : ''
                              } ${isActive ? 'text-success' : 'text-foreground hover:text-white'}`}
                            >
                              <span
                                className={`${isPostcodeDistrict ? '' : 'truncate'} ${
                                  isVehicleJobs ? 'underline decoration-dotted decoration-muted-foreground underline-offset-2' : ''
                                }`}
                              >
                                {h}
                              </span>
                              {isActive && (
                                sortDir === 'asc'
                                  ? <ChevronUp className="w-3.5 h-3.5 shrink-0" aria-hidden />
                                  : <ChevronDown className="w-3.5 h-3.5 shrink-0" aria-hidden />
                              )}
                            </button>
                            );
                            return isVehicleJobs
                              ? <AboveTooltip content={VEHICLE_JOBS_HEAD_HINT}>{sortButton}</AboveTooltip>
                              : sortButton;
                            })()}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {detailTable.rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={detailTable.heads.length}
                          className="px-6 py-10 text-center"
                        >
                          <div className="max-w-md mx-auto space-y-1.5">
                            <p className="text-sm font-medium text-foreground">{detailEmptyCopy.title}</p>
                            <p className="text-xs text-muted-foreground leading-relaxed">{detailEmptyCopy.body}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {pagedDetailRows.map((row, ri) => {
                      const detail = detailTable;
                      const rowMeta = pagedDetailMeta[ri];
                      const pinKey = `${detailDim}-${safeDetailPage}-${ri}`;
                      return (
                        <tr key={`${safeDetailPage}-${ri}`} className="border-b border-border/80 hover:bg-card/40">
                          {row.map((cell, ci) => {
                            const money = detail.moneyCols.includes(ci);
                            const neg = money && isNegativeMoneyCell(cell);
                            const pos = money && isPositiveMoneyCell(cell);
                            // Admin-only live MapPins on Vehicle/Job (mapPinCol set by mapper).
                            const showLiveMapPin =
                              isAdmin
                              && !demoMode
                              && detail.mapPinCol === ci
                              && rowMeta != null
                              && (rowMeta.kind === 'vehicle' || rowMeta.kind === 'job');
                            const showType = detail.typeCol === ci;
                            const showRunPill = detail.runPillCol === ci;
                            const mutedRun = showRunPill && (cell === 'Unknown' || cell === 'Unassigned');
                            const isDateCol = detail.heads[ci] === 'Date';
                            const isPostcodeDistrictCol = detail.heads[ci] === 'Postcode district';
                            let body: ReactNode = cell;
                            if (showLiveMapPin && rowMeta) {
                              const loading = mapPinLoadingKey === pinKey;
                              body = (
                                <span className="inline-flex max-w-full items-center gap-0.5 whitespace-nowrap">
                                  {/* Long telematics names (e.g. "WV76 CWC (DOODLE)") stay inside the column; full name on hover. */}
                                  <span className="min-w-0 truncate font-mono text-sm text-foreground" title={cell}>{cell}</span>
                                  <button
                                    type="button"
                                    disabled={loading}
                                    title={loading ? `Loading map for ${cell}…` : `Open map for ${cell}`}
                                    className="shrink-0 rounded p-0.5 text-ring/80 hover:text-ring hover:bg-ring/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void openMapFromMeta(rowMeta, pinKey);
                                    }}
                                  >
                                    <MapPin className={`w-3 h-3 ${loading ? 'animate-pulse' : ''}`} aria-hidden />
                                    <span className="sr-only">
                                      {loading ? `Loading map for ${cell}` : `Open map for ${cell}`}
                                    </span>
                                  </button>
                                </span>
                              );
                            } else if (detail.heads[ci] === 'Vehicle') {
                              body = (
                                <span className="block truncate font-mono text-sm text-foreground" title={cell}>
                                  {cell}
                                </span>
                              );
                            } else if (showType) {
                              // Job tab: Direction is the type column; COL / DEL / LOCAL get their own colours.
                              const pill = (detail.heads[ci] === DIRECTION_HEAD && DIRECTION_PILL[cell])
                                || 'bg-ring/15 text-ring border-ring/30';
                              body = (
                                <span className={`inline-block text-xs font-bold px-1.5 py-0.5 rounded border ${pill}`}>
                                  {cell}
                                </span>
                              );
                            } else if (showRunPill && !mutedRun) {
                              body = (
                                <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded-full bg-warning/10 text-warning border border-warning/45">
                                  {cell}
                                </span>
                              );
                            } else if (mutedRun) {
                              body = <span className="text-muted-foreground">{cell}</span>;
                            } else if (
                              (cell === NEAR_ZERO_MILES_CELL || cell === ZERO_MILES_CELL)
                              && detail.heads[ci] === 'Miles'
                            ) {
                              body = (
                                <span
                                  className="inline-block whitespace-nowrap text-xs font-bold px-1.5 py-0.5 rounded bg-accent/15 text-foreground border border-accent/40"
                                  title={cell === ZERO_MILES_CELL
                                    ? 'Telematics recorded no movement for this vehicle-day'
                                    : 'Under half a mile recorded for this vehicle-day'}
                                >
                                  {cell}
                                </span>
                              );
                            } else if (
                              detail.heads[ci] === DELIVERED_BY_HEAD
                              && rowMeta?.kind === 'job'
                              && missingVehicleKind(rowMeta.order as TruthMergedOrderRow) != null
                              && cell === missingVehicleLabel(rowMeta.order as TruthMergedOrderRow)
                            ) {
                              const gap = missingVehicleKind(rowMeta.order as TruthMergedOrderRow) === 'gap';
                              body = (
                                <span
                                  className={`inline-block whitespace-nowrap text-xs font-bold px-1.5 py-0.5 rounded border ${
                                    gap
                                      ? 'bg-warning/10 text-warning border-warning/40'
                                      : 'bg-accent/10 text-muted-foreground border-accent/30'
                                  }`}
                                  title={gap
                                    ? `${cell} in Palletways, but no tracking note put this job on a vehicle`
                                    : `${cell} in Palletways — not on one of our vehicles yet`}
                                >
                                  {cell}
                                </span>
                              );
                            } else if ((cell === 'DELETED' || cell === 'JOB CLOSED') && detail.heads[ci] === DELIVERED_BY_HEAD) {
                              body = (
                                <span
                                  className="inline-block whitespace-nowrap text-xs font-bold px-1.5 py-0.5 rounded bg-accent/15 text-foreground border border-muted-foreground/40"
                                  title={`${cell === 'DELETED' ? 'Deleted' : 'Closed'} in Palletways without being carried — left out of totals`}
                                >
                                  {cell}
                                </span>
                              );
                            } else if (cell === NO_JOBS_CELL && detail.heads[ci] === 'Jobs') {
                              body = (
                                <span
                                  className="inline-block whitespace-nowrap text-xs font-bold px-1.5 py-0.5 rounded bg-accent/15 text-foreground border border-accent/40"
                                  title="The vehicle drove with no jobs (empty running). Its cost counts in fleet totals, not in any job's profit."
                                >
                                  {cell}
                                </span>
                              );
                            } else if (cell === NO_GPS_CELL && detail.heads[ci] === 'Miles') {
                              body = (
                                <span
                                  className="inline-block whitespace-nowrap text-xs font-bold px-1.5 py-0.5 rounded bg-warning/10 text-warning border border-warning/40"
                                  title="No GPS data for this vehicle-day — mileage and mileage-based costs are unknown"
                                >
                                  {cell}
                                </span>
                              );
                            }
                            // Our trunk vehicle (hub ↔ depot) neither collected nor delivered the job:
                            // shown on hover in Delivered by, not as a third column.
                            const trunk = detail.heads[ci] === DELIVERED_BY_HEAD && rowMeta?.kind === 'job'
                              ? String((rowMeta.order as TruthMergedOrderRow).trunk_vehicle || '').trim()
                              : '';
                            if (trunk) body = <span title={`Trunk: ${trunk}`}>{body}</span>;
                            // Which figure a job's network charge or filled revenue used, in words (on click).
                            // Rows that sum several jobs (vehicle-day, customer) say how many used which figure.
                            const figureTitle = rowMeta?.kind === 'job'
                              ? jobFigureTitle(detail.heads[ci] ?? '', rowMeta.order as TruthMergedOrderRow)
                              : rowMeta?.figureTitles?.[detail.heads[ci] ?? ''];
                            // Shown on click only: detail for someone checking the number.
                            if (figureTitle) body = <FigureNote note={figureTitle}>{body}</FigureNote>;
                            // Customer names vary from a word to a paragraph: one line, full name on hover,
                            // so every row is the same height.
                            if (detail.heads[ci] === 'Customer' && cell) {
                              body = <span className="block truncate" title={String(cell)}>{body}</span>;
                            }
                            return (
                              <td
                                key={ci}
                                className={`py-1.5 px-1.5 tabular-nums ${
                                  isPostcodeDistrictCol ? 'px-2 whitespace-nowrap' : ''
                                } ${
                                  // ID + map pin never squeezed under the next column.
                                  showLiveMapPin ? 'whitespace-nowrap' : ''
                                } ${
                                  // "Depot 151" on one line, so rows stay one height.
                                  detail.heads[ci] === COLLECTED_BY_HEAD || detail.heads[ci] === DELIVERED_BY_HEAD
                                    ? 'whitespace-nowrap' : ''
                                } ${
                                  isDateCol ? 'text-muted-foreground' : money ? 'text-right' : 'text-foreground'
                                } ${neg ? 'text-destructive font-semibold' : pos ? 'text-success' : money ? 'text-foreground' : ''}`}
                              >
                                {body}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-background/50 text-foreground font-semibold">
                      {detailTable.foot.map((cell, ci) => {
                        const labelSpan = Math.max(1, detailTable.footLabelColSpan ?? 1);
                        // Skip cells covered by the leading foot label colspan.
                        if (ci > 0 && ci < labelSpan) return null;
                        const colSpan = ci === 0 && labelSpan > 1 ? labelSpan : undefined;
                        return (
                          <td
                            key={ci}
                            colSpan={colSpan}
                            className={`px-1.5 py-2 tabular-nums leading-tight ${
                              detailTable.moneyCols.includes(ci) ? 'text-right' : ''
                            } ${
                              ci === 0 && labelSpan > 1 ? 'whitespace-nowrap' : ''
                            } ${
                              figuresReady
                                && detailTable.moneyCols.includes(ci)
                                && isNegativeMoneyCell(cell)
                                ? 'text-destructive'
                                : figuresReady
                                  && detailTable.moneyCols.includes(ci)
                                  && isPositiveMoneyCell(cell)
                                  ? 'text-success'
                                  : ''
                            }`}
                          >
                            {figuresReady ? cell : ci === 0 ? cell : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
              {detailTable.note && (
                <p className="px-3 py-2 border-t border-border/60 text-xs text-warning/90">
                  {detailTable.note}
                </p>
              )}
              {/* Pipeline caveat (jobs awaiting delivery-day compute) — admin-only. */}
              {isAdmin && detailTable.adminNote && (
                <p className="px-3 py-2 border-t border-border/60 text-xs text-warning/90">
                  {detailTable.adminNote}
                </p>
              )}
              {sortedDetailRows.length > DETAIL_ROWS_PER_PAGE && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-3 border-t border-border/60 text-sm text-muted-foreground">
                  <span>
                    Showing {(safeDetailPage - 1) * DETAIL_ROWS_PER_PAGE + 1}
                    {' to '}
                    {Math.min(safeDetailPage * DETAIL_ROWS_PER_PAGE, sortedDetailRows.length)}
                    {' of '}
                    {sortedDetailRows.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={safeDetailPage <= 1}
                      onClick={() => setDetailPage((p) => Math.max(1, p - 1))}
                      className="px-2 py-1 rounded bg-secondary disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <span className="text-foreground">
                      Page {safeDetailPage} of {detailTotalPages}
                    </span>
                    <button
                      type="button"
                      disabled={safeDetailPage >= detailTotalPages}
                      onClick={() => setDetailPage((p) => Math.min(detailTotalPages, p + 1))}
                      className="px-2 py-1 rounded bg-secondary disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              {demoMode && notesOn && (
                <p className="text-xs text-chart-10/90 border-t border-border/60 bg-chart-10/5 px-3 py-2.5">
                  {display.notes.detail}
                </p>
              )}
            </div>
          </section>
        </>

      {mapModal && (
        <ProfitVehicleDayMapModal
          key={`${mapModal.row.vehicleId}-${mapModal.row.date}-${mapModal.order?.orderId || 'vehicle'}`}
          row={mapModal.row}
          order={mapModal.order}
          stops={mapModal.stops}
          segments={mapModal.segments}
          orgDepots={orgDepotsForMap}
          orders={mapOrders}
          orderCoordsPending={ordersCoordsPending}
          mergedCosts={mapMergedCosts}
          telematicsLoadError={mapModal.telematicsLoadError}
          onClose={() => setMapModal(null)}
        />
      )}
    </div>
  );
}
